import { assertUncomputedFromBase, fromBaseRecord, FromBaseInputMissing } from '../janus-engine/compile/from-base-context';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { createNpcDefinitionFinalizer } from '../janus-engine/compile/from-base-npc-finalization';
import { fromBaseCatalogFinalizationGaps } from '../janus-engine/compile/from-base-definition-finalization';
import { inventoryCatalogScope, mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import type { InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import { itemsCol, layerOf, layerHas, layerEntriesOf, layerListEntryUuid } from '../shared/array-types';
import { phpRound } from '../janus-engine/pl/php-round';

interface PlReviewInput {
    schema: 'pl-review-definitions-v1';
    family: 'spell' | 'creature';
    selected: FromBaseRow[];
    base: FromBaseRow;
    shared: InventorySharedCatalog;
    stored_pl: Record<string, number | null>;
}

/** One native finalizer for the complete selection; missing inputs stay visible as selected rows. */
export async function projectPlReviewDefinitions(input: PlReviewInput) {
    if (input.schema !== 'pl-review-definitions-v1' || !['spell', 'creature'].includes(input.family))
        throw new Error('pl-review-definitions-schema');
    assertUncomputedFromBase(input.base);
    assertUncomputedFromBase(input.shared);
    const base = inventoryCatalogScope(input.shared, input.base).base;
    const metadata = entityRuntimeMetadata(input.shared.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
    const inventory = mergeInventoryMetadata(metadata.inventory, base.inventory_metadata);
    const finalize = createNpcDefinitionFinalizer(base, metadata.player, inventory);
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const definitions = new Map<string, FromBaseRow>();
    for (const [id, row] of Object.entries(catalog)) if (fromBaseRecord(row)) definitions.set(id.toUpperCase(), row);
    const referenceName = (definition: FromBaseRow, layer: string): string => {
        const entry: unknown = layerEntriesOf(definition, layer)[0];
        const id = (layerListEntryUuid(entry) || layerListEntryUuid(itemsCol(entry, 'item_id'))).toUpperCase();
        return String(itemsCol(entry, 'item_name') || itemsCol(definitions.get(id), 'item_name') || '');
    };
    const rows = input.selected.map(selected => {
        const id = String(itemsCol(selected, 'item_id') ?? '').toUpperCase();
        const storedValue = input.stored_pl[id];
        const stored = typeof storedValue === 'number' && Number.isFinite(storedValue) ? phpRound(storedValue, 2) : null;
        let definition = definitions.get(id) ?? {}, diagnostic = '';
        try {
            const compiled = finalize.definition(id, []);
            if (!compiled) throw new FromBaseInputMissing('root-definition', id);
            definition = compiled;
            diagnostic = fromBaseCatalogFinalizationGaps(base, inventory, compiled).join('; ');
        } catch (error) {
            // Metadata failure must not erase the selected identity or its saved PL.
            diagnostic = error instanceof Error ? error.message : String(error);
        }
        const row = { id, name: String(itemsCol(selected, 'item_name') ?? ''), pl: stored, stored, driver: '',
            definition_diagnostic: diagnostic };
        if (input.family === 'spell') return { ...row,
            level: Math.trunc(Number(layerOf(definition, 'spell_level') ?? 0)),
            circle: referenceName(definition, 'spell_circle'),
            cls: layerHas(definition, 'requires_class') || layerHas(definition, 'requires_subclass') };
        const raw = layerOf<unknown>(definition, 'cr');
        const cr = raw === undefined || raw === null || raw === '' ? null : Number(raw);
        return { ...row, cr, crk: cr === null ? '' : String(phpRound(cr, 3)), no_cr: cr === null,
            type: referenceName(definition, 'creature_type'), role: referenceName(definition, 'creature_role') };
    });
    const filters = [...new Set(rows.map(row => 'level' in row ? String(row.level) : row.crk).filter(value => value !== ''))]
        .sort((a, b) => Number(a) - Number(b));
    return { rows, filters };
}
